import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/*
 * Drop a spreadsheet here, or choose one. Reads it as base64 for
 * POST /imports (the file travels in the request body; see
 * routes/bulk_import.py for why it is not uploaded to object storage).
 * Size and type are checked before reading, so a wrong file fails at once.
 *
 * `accept` widens or narrows the extensions taken (PITAX's export comes as a
 * .zip package); `hint`, `wrongType` and `tip` then say so in the page's
 * words. Each defaults to the spreadsheet wording.
 */
export const MAX_BYTES = 4000000;
export const ACCEPTED = ['.xlsx', '.csv'];
/** What a PITAX import takes: its .zip package, the workbook inside it, or a
 *  sheet already in its columns (routes/bulk_import.py upload). */
export const PITAX_ACCEPTED = ['.zip', '.xlsx', '.csv'];

export function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',').pop());
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function FileDropzone({ onFile, disabled = false, accept: extensions = ACCEPTED, hint, wrongType, tip }) {
  const { t } = useTranslation('import');
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const [problem, setProblem] = useState('');

  const accept = async (file) => {
    if (!file) return;
    setProblem('');
    const name = file.name.toLowerCase();
    if (!extensions.some(ext => name.endsWith(ext))) {
      setProblem(wrongType || t('dropzone.wrongType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setProblem(t('dropzone.tooLarge'));
      return;
    }
    try {
      const base64 = await readAsBase64(file);
      onFile({ name: file.name, size: file.size, base64 });
    } catch {
      setProblem(t('dropzone.readFailed'));
    }
  };

  return (
    <div>
      <div
        data-testid="import-dropzone"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        title={tip || t('tip.dropzone')}
        onClick={() => !disabled && input.current?.click()}
        onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); input.current?.click(); } }}
        onDragOver={e => { e.preventDefault(); if (!disabled) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); if (!disabled) accept(e.dataTransfer?.files?.[0]); }}
        style={{
          border: `2px dashed ${over ? 'var(--c-primary)' : 'var(--c-border-strong)'}`,
          background: over ? 'var(--c-primary-soft)' : 'var(--c-bg-subtle)',
          borderRadius: 10, padding: '22px 16px', textAlign: 'center', cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div style={{ fontSize: 28 }} aria-hidden="true">📄</div>
        <div style={{ fontWeight: 600, marginTop: 4 }}>{t('dropzone.prompt')}</div>
        <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', marginTop: 4 }}>{hint || t('dropzone.hint')}</div>
        <input ref={input} type="file" accept={extensions.join(',')} aria-label={t('dropzone.choose')}
               data-testid="import-file-input" style={{ display: 'none' }}
               onChange={e => { accept(e.target.files?.[0]); e.target.value = ''; }} />
      </div>
      {problem && <p role="alert" style={{ color: 'var(--c-danger-fg)', fontSize: 13, margin: '6px 0 0' }}>{problem}</p>}
    </div>
  );
}
